'use client';

import { useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { sanitizeUrl } from '@bridge/utils';

import { isPaginationCursorParam } from '../utils/pagination-params';
import { useTableFilterStore } from './store';
import { removeSearchParamsFromUrl } from './url';

import type { FilterType } from './store';

/**
 * Hook for managing table filters with global state and URL synchronization
 * Provides methods to clear filters and access active filter state
 * @param filterType - Whether to use RSC (router.replace) or client-side (window.history) routing
 */
export function useTableFilters(filterType: FilterType = 'rsc') {
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    activeFilterParams,
    setActiveFilters,
    getActiveFilters,
    updateActiveFiltersFromConfig,
    releaseActiveFilters,
    setFilterType,
    setRouter,
    updateFilterValue,
    updateDateRangeFilter,
    updateMultiSelectFilter,
    updateMultiSelectFlatFilter,
  } = useTableFilterStore();

  // Initialize refs with current URL state to prevent false change detection on initial load
  const initializeRefs = () => {
    if (!searchParams) {
      return { filters: [], values: {} };
    }

    const initialFilterValues: Record<string, string> = {};
    activeFilterParams.forEach((param) => {
      const value = searchParams.get(param);
      if (value !== null) {
        initialFilterValues[param] = value;
      }
    });

    return {
      filters: [...activeFilterParams],
      values: initialFilterValues,
    };
  };

  const { filters: initialFilters, values: initialValues } = initializeRefs();

  // Keep track of previous filter params and values to detect changes
  const prevActiveFiltersRef = useRef<string[]>(initialFilters);
  const prevFilterValuesRef = useRef<Record<string, string>>(initialValues);

  // Set the filter type and router in the store on mount/change
  useEffect(() => {
    setFilterType(filterType);
    setRouter(router);
  }, [filterType, router, setFilterType, setRouter]);

  /**
   * Get all cursor parameter names from current search params
   */
  function getCursorParams(): string[] {
    if (!searchParams) return [];

    const cursorParams: string[] = [];
    searchParams.forEach((_, key) => {
      if (isPaginationCursorParam(key)) {
        cursorParams.push(key);
      }
    });
    return cursorParams;
  }

  /**
   * Clear specific filter parameters or all active filters
   * @param filterParams - Optional array of specific params to clear. If not provided, clears all active filters
   */
  function clearFilters(filterParams?: string[]) {
    if (!filterParams || filterParams.length === 0) return;

    const newUrl = removeSearchParamsFromUrl(window.location.href, filterParams);
    if (filterType === 'client') {
      window.history.replaceState(null, '', sanitizeUrl(newUrl));
    } else if (router) {
      router.replace(sanitizeUrl(newUrl));
    }
  }

  /**
   * Clear all active filters and cursor parameters
   */
  function clearAllFilters() {
    const filterParams = activeFilterParams;
    const cursorParams = getCursorParams();
    const allParamsToRemove = [...filterParams, ...cursorParams];
    clearFilters(allParamsToRemove);
  }

  // Watch for filter changes and clear cursor params only when filters change
  useEffect(() => {
    if (!searchParams) return;

    // Check if filters have actually changed
    const prevFilters = prevActiveFiltersRef.current;
    const currentFilters = activeFilterParams;

    // Get current filter values
    const currentFilterValues: Record<string, string> = {};
    currentFilters.forEach((param) => {
      const value = searchParams.get(param);
      if (value !== null) {
        currentFilterValues[param] = value;
      }
    });

    // Compare filter arrays to detect changes in active filters
    const filterNamesChanged =
      prevFilters.length !== currentFilters.length ||
      prevFilters.some((filter, index) => filter !== currentFilters[index]) ||
      currentFilters.some((filter, index) => filter !== prevFilters[index]);

    // Compare filter values to detect changes in filter values
    const filterValuesChanged =
      Object.keys(currentFilterValues).some((key) => prevFilterValuesRef.current[key] !== currentFilterValues[key]) ||
      Object.keys(prevFilterValuesRef.current).some((key) => !(key in currentFilterValues));

    const filtersChanged = filterNamesChanged || filterValuesChanged;

    // Only clear cursor params if filters have changed
    if (filtersChanged) {
      clearFilters(getCursorParams());
    }

    // Update the previous filters and values references
    prevActiveFiltersRef.current = [...currentFilters];
    prevFilterValuesRef.current = { ...currentFilterValues };
  }, [activeFilterParams, searchParams, router]);

  return {
    activeFilterParams,
    setActiveFilters,
    clearFilters,
    clearAllFilters,
    getActiveFilters,
    updateActiveFiltersFromConfig,
    releaseActiveFilters,
    updateFilterValue,
    updateDateRangeFilter,
    updateMultiSelectFilter,
    updateMultiSelectFlatFilter,
  };
}
